/** @jsx jsx */
import React, { useContext, useState } from 'react';
import { jsx } from '@emotion/core';
import { useTranslation } from 'react-i18next';
import { Grid, Typography, useMediaQuery, useTheme } from '@material-ui/core';
import { useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

import StartIcon from 'assets/icons/start';
import FirstStepIcon from 'assets/icons/firstStep';
import SecondStepIcon from 'assets/icons/secondStep';
import ThirdStepIcon from 'assets/icons/thirdStep';
import {
  GoalType,
  ChallengeGoalTypes,
  ChallengeResultType,
  ICustomTheme,
} from 'interfaces';
import { ChallengeSection, CountUp, SlantedBanner, Stepper } from 'components';
import { StepType } from 'components/Stepper';
import { Text as StepperText } from 'components/Stepper/styles';
import { currencySelector } from 'reducers/app.reducer';
import { GOAL } from 'constants/challenges';
import { getFormatedDates } from 'core/utils/date.helpers';
import GenericGoalResultBanner from '../GenericGoalResultBanner';
import * as S from './styles';
import { isRewardingAutoSelector } from 'reducers/challenge.reducer';

const {
  cssBannerContainer,
  cssCustomStepper,
  cssMarginTop,
  cssPercentBlock,
  cssStepperContainer,
  getCssResultStep,
} = S;

const icons: JSX.Element[] = [
  <FirstStepIcon fontSize="inherit" />,
  <SecondStepIcon fontSize="inherit" />,
  <ThirdStepIcon fontSize="inherit" />,
];

type ChallengeResultsTypes = {
  areResultsVisible: boolean;
  currentGroupTab?: number;
  disabled: boolean;
  goals?: ChallengeGoalTypes[];
  goalType: GoalType;
  unitToDisplay?: string | null;
  isGroup?: boolean;
  onChangeTab?: (event: React.ChangeEvent<unknown>, value: number) => void;
  userChallengeResults: Partial<ChallengeResultType>[] | undefined;
  isGoalMecanicByProduct?: boolean;
};

const ChallengeResults = ({
  areResultsVisible,
  currentGroupTab,
  disabled,
  goals,
  goalType,
  unitToDisplay,
  isGroup,
  onChangeTab,
  userChallengeResults,
  isGoalMecanicByProduct = false,
}: ChallengeResultsTypes): JSX.Element => {
  const { t } = useTranslation();
  const { id: challengeId }: { id: string } = useParams();
  const theme: ICustomTheme = useTheme();
  const isDownSm = useMediaQuery(theme.breakpoints.down('sm'));
  const isDownXs = useMediaQuery(theme.breakpoints.down('xs'));
  const currency = useSelector(currencySelector);
  const isRewardingAuto = useSelector(
    isRewardingAutoSelector(Number(challengeId)),
  );
  const [firstStepRef, setFirstStepRef] = useState<HTMLDivElement | null>(null);

  /*
      The group concept is linked to 'Defis' challenges only
      A user can have several results depending on his groups
      A single result is displayed but a system of tabs allows you to change your choice
  */
  const userChallengeResult = userChallengeResults?.[currentGroupTab || 0];
  const userChallengeResultValueToDisplay = isGoalMecanicByProduct
    ? userChallengeResult?.challengeRewardValue
    : userChallengeResult?.resultValue;

  let content = <span />;
  switch (goalType) {
    case GOAL.RESULT:
    case GOAL.CUSTOM:
    case GOAL.BY_PROFILE:
      if (!disabled) {
        content = (
          <Grid container justify="center" css={cssBannerContainer}>
            <Grid item css={{ display: 'flex' }}>
              <SlantedBanner
                isHighlighted
                isLarge={!isDownSm}
                isLeftHidden={!areResultsVisible}
                leftText={
                  <strong>
                    {userChallengeResult?.rank || '-'}
                    <sup>e</sup>
                  </strong>
                }
                rightText={
                  <React.Fragment>
                    <strong>
                      {userChallengeResult?.resultValue !== undefined &&
                      userChallengeResult?.resultValue !== null ? (
                          <CountUp
                          end={Number(userChallengeResultValueToDisplay)}
                        />
                        ) : (
                          '-'
                        )}
                      {(goalType === GOAL.CUSTOM ||
                        goalType === GOAL.BY_PROFILE) && (
                        <sub>
                          <small>
                            /
                            {userChallengeResult?.customResultValue?.toLocaleString()}
                          </small>
                        </sub>
                      )}
                    </strong>
                    <br />
                    {unitToDisplay && (
                      <small>
                        <small>{unitToDisplay}</small>
                      </small>
                    )}
                    {goalType === GOAL.CUSTOM && goals?.[0]?.unit && (
                      <small>
                        <small>{goals[0].unit}</small>
                      </small>
                    )}
                    {goalType === GOAL.BY_PROFILE &&
                      userChallengeResult?.customResultUnit && (
                      <small>
                        <small>{userChallengeResult.customResultUnit}</small>
                      </small>
                    )}
                  </React.Fragment>
                }
              />
              {(goalType === GOAL.CUSTOM || goalType === GOAL.BY_PROFILE) && (
                <Typography
                  color="primary"
                  variant={isDownSm ? 'h5' : 'h3'}
                  css={cssPercentBlock}
                >
                  <strong>
                    <CountUp
                      end={
                        (Number(userChallengeResult?.resultValue) /
                          Number(userChallengeResult?.customResultValue)) *
                        100
                      }
                    />
                    %
                  </strong>
                </Typography>
              )}
            </Grid>
          </Grid>
        );
      }
      break;
    case GOAL.GENERIC: {
      const goalMapper = (
        goal: ChallengeGoalTypes,
        index: number,
      ): StepType => {
        let status: 'start' | 'first' | 'done' | 'next' | 'disabled' =
          'disabled';
        if (goal?.value && userChallengeResult?.resultValue !== undefined) {
          if (goal.value <= userChallengeResult.resultValue) {
            status = 'done';
          }
          if (goal.value > userChallengeResult.resultValue) {
            status = 'next';
            const prevGoalValue = goals && goals[index - 1]?.value;
            if (
              (prevGoalValue &&
                prevGoalValue > userChallengeResult.resultValue) ||
              disabled
            ) {
              status = 'disabled';
            }
          }
        }
        return { ...goal, status, icon: icons[index] };
      };

      const step0: StepType = {
        '@id': 'id',
        '@type': 'type',
        name: 'name',
        subject: 'subject',
        unit: 'unit',
        status: 'first',
        value: 0,
        icon: <StartIcon fontSize="inherit" />,
      };
      const goalsWithStatus = goals?.length ? goals.map(goalMapper) : [];
      const steps: StepType[] = [step0, ...goalsWithStatus];

      const currentStep = steps.reduce(
        (acc, curr, index) => (curr.status === 'done' ? index : acc),
        0,
      );
      const progress =
        userChallengeResult?.resultValue !== undefined &&
        steps[currentStep]?.value !== undefined &&
        steps[currentStep + 1]?.value !== undefined
          ? ((Number(userChallengeResult?.resultValue) -
              Number(steps[currentStep].value)) /
              (Number(steps[currentStep + 1].value) -
                Number(steps[currentStep].value))) *
            100
          : 0;

      const activeStep = currentStep + 1 || undefined;
      let bannerStyle = { left: 0, transform: 'unset' };
      if (activeStep !== undefined && firstStepRef) {
        const { clientWidth: stepWidth } = firstStepRef;
        // position the banner according to progression
        const left =
          theme.spacing(3) + // stepper padding
          activeStep * stepWidth +
          stepWidth * (progress / 100) -
          stepWidth / 2;
        bannerStyle = { left, transform: 'translateX(-50%)' };
      }
      const multi =
        0.35 + 0.4 * Math.min(activeStep || 1, (steps?.length || 1) - 1);
      /* empirical way to keep the slanted banner in the frame on mobile */
      const containerStyle =
        isDownXs &&
        userChallengeResult?.resultValue &&
        userChallengeResult.resultValue > 0
          ? { marginLeft: -(bannerStyle.left * multi) }
          : {};

      content = (
        <div css={cssStepperContainer}>
          <Stepper
            AboveStepContent={() => (
              <div css={{ marginTop: theme.spacing(9.75) }} />
            )}
            BelowStepContent={({ step }: { step: StepType }) => (
              <div>
                {step.status !== 'first' && (
                  <StepperText className={step.status}>
                    <Grid container justifyContent="center" spacing={1}>
                      <Grid item>
                        {isRewardingAuto && (
                          <StepperText className={step.status}>
                            {step.challengeReward?.value?.toLocaleString()}
                            &nbsp;
                            {currency}
                          </StepperText>
                        )}
                        <Grid item>
                          <StepperText className={step.status}>
                            {step.value?.toLocaleString()} {steps?.[1].unit}
                          </StepperText>
                        </Grid>
                      </Grid>
                    </Grid>
                  </StepperText>
                )}
              </div>
            )}
            connector={
              <S.CustomConnector
                value={progress}
                top={95}
                disabled={disabled}
                numberOfStep={steps.length}
              />
            }
            containerStyle={containerStyle}
            currentStep={currentStep}
            disabled={disabled}
            setFirstStepRef={setFirstStepRef}
            steps={steps}
            stepStyleFn={getCssResultStep}
            css={cssCustomStepper}
          >
            <GenericGoalResultBanner
              currentStep={currentStep}
              isLeftHidden={!areResultsVisible}
              result={userChallengeResult}
              steps={steps}
              style={bannerStyle}
            />
          </Stepper>
        </div>
      );
      break;
    }
    default:
      // eslint-disable-next-line no-console
      console.error('Wrong goal type received in ChallengeResults: ', goalType);
  }

  return (
    <React.Fragment>
      {isGroup && userChallengeResults && userChallengeResults.length > 0 && (
        <S.Tabs
          value={currentGroupTab}
          onChange={onChangeTab}
          indicatorColor="primary"
          variant="scrollable"
          scrollButtons="auto"
        >
          {userChallengeResults.map((result) => (
            <S.Tab key={result.id} label={result.entity?.name} />
          ))}
        </S.Tabs>
      )}
      {disabled || userChallengeResult?.resultValue == null ? (
        <Typography css={cssMarginTop}>{t('challenges.no_result')}</Typography>
      ) : (
        content
      )}
    </React.Fragment>
  );
};

export default ChallengeResults;

interface IChallengeResultsSection {
  hasResult: boolean;
  date?: string | null;
  children: any;
}

export function ChallengeResultsSection({
  children,
  hasResult,
  date,
}: IChallengeResultsSection) {
  const { t } = useTranslation();
  return (
    <ChallengeSection
      defaultExpanded={hasResult}
      title={
        <span
          dangerouslySetInnerHTML={{
            __html: date
              ? t('challenges.detail.results.title', {
                date: getFormatedDates(date).string,
              })
              : t('challenges.detail.results.title_no_date'),
          }}
        />
      }
    >
      <S.Section>{children}</S.Section>
    </ChallengeSection>
  );
}
